import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Cursor, Rectangle, Circle, LineSegment, Polygon, PencilSimple, TextT,
  ArrowRight, ArrowUUpLeft, ArrowUUpRight,
  AlignLeft, AlignCenterHorizontal, AlignRight, AlignTop, AlignCenterVertical, AlignBottom,
  Rows, Columns, Stack, Eye, EyeSlash, Lock, LockOpen, Copy, Trash,
  FloppyDisk, Download, Lightning, X, Plus, GridFour,
  MagnifyingGlassMinus, MagnifyingGlassPlus, Hand, FrameCorners, BezierCurve,
  ArrowElbowRight, ArrowLineRight, Selection, MagicWand, Path,
  Unite, Subtract, Intersect, Exclude, Gradient, Drop, Sparkle,
  TextAa, CursorClick, Pen
} from '@phosphor-icons/react';
import {
  TOOLS, COLOR_PALETTE, FONT_OPTIONS, DASH_STYLES, LINE_CAPS, LINE_JOINS,
  ARROW_STYLES, VARIABLE_WIDTH_PROFILES, BIO_TEMPLATES, getBBox
} from './editorHelpers';
import { BLEND_MODES, EFFECT_PRESETS, PATTERN_TYPES } from './advancedFeatures';

// ==================== TOP TOOLBAR ====================
export const EditorToolbar = ({
  tool, setTool, zoom, setZoom, gridEnabled, setGridEnabled,
  snapEnabled, setSnapEnabled, showRulers, setShowRulers,
  undo, redo, canUndo, canRedo, selectedIds, objects,
  onAlign, onDistribute, onGroup, onUngroup, onDelete, onDuplicate,
  onBoolean,
  figureName, setFigureName, onSave, onExport, onAIToggle, showAI
}) => {
  const toolGroups = [
    [
      { id: TOOLS.SELECT, icon: Cursor, tip: 'Select (V)' },
      { id: TOOLS.DIRECT_SELECT, icon: CursorClick, tip: 'Direct Select (D)' },
      { id: TOOLS.LASSO, icon: Selection, tip: 'Lasso (Q)' },
      { id: TOOLS.MAGIC_WAND, icon: MagicWand, tip: 'Magic Wand (W)' },
    ],
    [
      { id: TOOLS.RECT, icon: Rectangle, tip: 'Rectangle (R)' },
      { id: TOOLS.ELLIPSE, icon: Circle, tip: 'Ellipse (E)' },
      { id: TOOLS.LINE, icon: LineSegment, tip: 'Line (L)' },
      { id: TOOLS.POLYGON, icon: Polygon, tip: 'Polygon' },
    ],
    [
      { id: TOOLS.PEN, icon: PencilSimple, tip: 'Pen (P)' },
      { id: TOOLS.CURVATURE, icon: Path, tip: 'Curvature (C)' },
      { id: TOOLS.ANCHOR_POINT, icon: Pen, tip: 'Anchor Point - Reshape (Shift+C)' },
      { id: TOOLS.TEXT, icon: TextT, tip: 'Text (T)' },
    ],
    [
      { id: TOOLS.ARROW, icon: ArrowRight, tip: 'Arrow (A)' },
      { id: TOOLS.CONNECTOR_STRAIGHT, icon: ArrowLineRight, tip: 'Straight Connector' },
      { id: TOOLS.CONNECTOR_CURVED, icon: BezierCurve, tip: 'Curved Connector' },
      { id: TOOLS.CONNECTOR_ELBOW, icon: ArrowElbowRight, tip: 'Elbow Connector' },
    ],
    [
      { id: TOOLS.ARTBOARD, icon: FrameCorners, tip: 'Artboard (F)' },
      { id: TOOLS.BLEND, icon: Gradient, tip: 'Blend (B)' },
      { id: TOOLS.PAN, icon: Hand, tip: 'Pan (H)' },
    ],
  ];

  return (
    <div className="flex items-center gap-1 bg-white border-b border-slate-200 px-3 py-1.5 sticky top-0 z-20">
      <Input value={figureName} onChange={(e) => setFigureName(e.target.value)}
        className="input-field w-40 text-sm font-medium h-7 mr-2" data-testid="figure-name-input" />
      <div className="w-px h-5 bg-slate-200 mx-0.5" />

      {/* Tool groups */}
      {toolGroups.map((group, gi) => (
        <React.Fragment key={gi}>
          <div className="flex gap-0.5">
            {group.map(t => (
              <button key={t.id} onClick={() => setTool(t.id)} title={t.tip}
                className={`p-1.5 rounded-md transition-all ${tool === t.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'}`}
                data-testid={`tool-${t.id}`}>
                <t.icon size={14} weight={tool === t.id ? 'bold' : 'regular'} />
              </button>
            ))}
          </div>
          {gi < toolGroups.length - 1 && <div className="w-px h-5 bg-slate-200 mx-0.5" />}
        </React.Fragment>
      ))}

      <div className="w-px h-5 bg-slate-200 mx-0.5" />

      {/* Undo / Redo */}
      <button onClick={undo} disabled={!canUndo} className="p-1.5 text-slate-400 hover:text-slate-200 disabled:opacity-30 rounded-md hover:bg-slate-700" title="Undo (Ctrl+Z)">
        <ArrowUUpLeft size={14} />
      </button>
      <button onClick={redo} disabled={!canRedo} className="p-1.5 text-slate-400 hover:text-slate-200 disabled:opacity-30 rounded-md hover:bg-slate-700" title="Redo (Ctrl+Y)">
        <ArrowUUpRight size={14} />
      </button>

      {/* Boolean ops - visible when 2 selected */}
      {selectedIds.length === 2 && (
        <>
          <div className="w-px h-5 bg-slate-200 mx-0.5" />
          <button onClick={() => onBoolean('unite')} className="p-1.5 text-slate-400 hover:text-indigo-400 rounded-md hover:bg-slate-700" title="Unite" data-testid="bool-unite"><Unite size={14} /></button>
          <button onClick={() => onBoolean('subtract')} className="p-1.5 text-slate-400 hover:text-indigo-400 rounded-md hover:bg-slate-700" title="Minus Front" data-testid="bool-subtract"><Subtract size={14} /></button>
          <button onClick={() => onBoolean('intersect')} className="p-1.5 text-slate-400 hover:text-indigo-400 rounded-md hover:bg-slate-700" title="Intersect" data-testid="bool-intersect"><Intersect size={14} /></button>
          <button onClick={() => onBoolean('exclude')} className="p-1.5 text-slate-400 hover:text-indigo-400 rounded-md hover:bg-slate-700" title="Exclude" data-testid="bool-exclude"><Exclude size={14} /></button>
        </>
      )}

      {/* Alignment */}
      {selectedIds.length >= 2 && (
        <>
          <div className="w-px h-5 bg-slate-200 mx-0.5" />
          <button onClick={() => onAlign('left')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Align Left"><AlignLeft size={13} /></button>
          <button onClick={() => onAlign('center-h')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Align Center"><AlignCenterHorizontal size={13} /></button>
          <button onClick={() => onAlign('right')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Align Right"><AlignRight size={13} /></button>
          <button onClick={() => onAlign('top')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Align Top"><AlignTop size={13} /></button>
          <button onClick={() => onAlign('center-v')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Align Middle"><AlignCenterVertical size={13} /></button>
          <button onClick={() => onAlign('bottom')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Align Bottom"><AlignBottom size={13} /></button>
          <button onClick={() => onDistribute('h')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Distribute H"><Columns size={13} /></button>
          <button onClick={() => onDistribute('v')} className="p-1 text-slate-400 hover:text-indigo-400 rounded" title="Distribute V"><Rows size={13} /></button>
        </>
      )}

      <div className="flex-1" />

      {/* Toggles */}
      <button onClick={() => setGridEnabled(!gridEnabled)} className={`p-1.5 rounded-md ${gridEnabled ? 'bg-indigo-900/40 text-indigo-300' : 'text-slate-400'}`}><GridFour size={13} /></button>
      <button onClick={() => setSnapEnabled(!snapEnabled)} className={`p-1 rounded-md text-[9px] font-bold ${snapEnabled ? 'bg-indigo-900/40 text-indigo-300' : 'text-slate-400'}`}>SN</button>
      <button onClick={() => setShowRulers(!showRulers)} className={`p-1 rounded-md text-[9px] font-bold ${showRulers ? 'bg-indigo-900/40 text-indigo-300' : 'text-slate-400'}`}>RL</button>

      <div className="w-px h-5 bg-slate-200 mx-0.5" />
      <button onClick={() => setZoom(Math.max(0.1, zoom - 0.25))} className="p-1 text-slate-400 rounded"><MagnifyingGlassMinus size={13} /></button>
      <span className="text-[10px] text-slate-400 w-9 text-center font-mono">{Math.round(zoom * 100)}%</span>
      <button onClick={() => setZoom(Math.min(8, zoom + 0.25))} className="p-1 text-slate-400 rounded"><MagnifyingGlassPlus size={13} /></button>

      <div className="w-px h-5 bg-slate-200 mx-0.5" />
      <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={onAIToggle}>
        <Lightning size={12} className={`mr-1 ${showAI ? 'text-indigo-400' : ''}`} /> AI
      </Button>
      <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={onExport} data-testid="export-btn">
        <Download size={12} className="mr-1" /> Export
      </Button>
      <Button size="sm" className="btn-primary h-7 text-[11px] px-2" onClick={onSave} data-testid="save-figure-btn">
        <FloppyDisk size={12} className="mr-1" /> Save
      </Button>
    </div>
  );
};

// ==================== LEFT PANEL ====================
export const EditorLeftPanel = ({
  currentStyle, onStyleChange, selectedObj, onUpdateObject,
  onApplyGradient, onApplyPattern, onApplyEffect, onRemoveEffect,
  symbols, onSaveSymbol, onInstantiateSymbol
}) => {
  const [activeSection, setActiveSection] = useState('fill');

  const handleChange = (key, value) => {
    onStyleChange(key, value);
    if (selectedObj) onUpdateObject(selectedObj.id, { [key]: value });
  };

  const sections = ['fill', 'stroke', 'effects', 'type', 'symbols'];

  return (
    <div className="w-52 bg-white border-r border-slate-200 overflow-y-auto flex-shrink-0" style={{ height: 'calc(100vh - 200px)' }}>
      {/* Section tabs */}
      <div className="flex border-b border-slate-200 sticky top-0 bg-white z-10">
        {sections.map(s => (
          <button key={s} onClick={() => setActiveSection(s)}
            className={`flex-1 px-1 py-1.5 text-[9px] uppercase tracking-wider font-semibold ${activeSection === s ? 'text-indigo-300 border-b-2 border-indigo-400' : 'text-slate-400'}`}>
            {s}
          </button>
        ))}
      </div>

      {/* FILL SECTION */}
      {activeSection === 'fill' && (
        <div className="p-3 space-y-3">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Solid Fill</span>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={currentStyle.fill === 'none' ? '#ffffff' : currentStyle.fill} onChange={(e) => handleChange('fill', e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border border-slate-200 p-0" />
              <input value={currentStyle.fill} onChange={(e) => handleChange('fill', e.target.value)}
                className="input-field flex-1 text-[10px] font-mono px-2 py-1" />
              <button onClick={() => handleChange('fill', 'none')} className="text-[9px] text-slate-400 hover:text-red-500 px-1"><X size={10} /></button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {COLOR_PALETTE.map(c => (
                <button key={c} onClick={() => handleChange('fill', c)}
                  className="w-4 h-4 rounded-sm border border-slate-200 hover:scale-125 transition-transform" style={{ background: c }} />
              ))}
            </div>
          </div>

          {/* Gradient */}
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Gradient</span>
            <div className="grid grid-cols-2 gap-1 mt-1.5">
              <button onClick={() => onApplyGradient('linear')} className="px-2 py-1.5 text-[10px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-300 hover:text-indigo-300" data-testid="apply-linear-gradient">Linear</button>
              <button onClick={() => onApplyGradient('radial')} className="px-2 py-1.5 text-[10px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-300 hover:text-indigo-300" data-testid="apply-radial-gradient">Radial</button>
            </div>
            {selectedObj?.gradient && (
              <div className="mt-2 p-2 bg-slate-800 rounded-md space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-slate-400 capitalize">{selectedObj.gradient.type} gradient</span>
                  <button onClick={() => onUpdateObject(selectedObj.id, { gradient: null })} className="text-[9px] text-red-400 hover:text-red-600">Remove</button>
                </div>
                {selectedObj.gradient.type === 'linear' && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] text-slate-400">Angle</span>
                    <input type="range" min="0" max="360" value={selectedObj.gradient.angle || 0}
                      onChange={(e) => {
                        const angle = Number(e.target.value);
                        const rad = (angle * Math.PI) / 180;
                        onUpdateObject(selectedObj.id, { gradient: { ...selectedObj.gradient, angle, x1: 50 - 50 * Math.cos(rad), y1: 50 - 50 * Math.sin(rad), x2: 50 + 50 * Math.cos(rad), y2: 50 + 50 * Math.sin(rad) } });
                      }} className="flex-1 accent-teal-600" />
                    <span className="text-[9px] text-slate-400 w-6">{selectedObj.gradient.angle || 0}</span>
                  </div>
                )}
                {(selectedObj.gradient.stops || []).map((stop, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <input type="color" value={stop.color} onChange={(e) => {
                      const stops = [...selectedObj.gradient.stops];
                      stops[i] = { ...stops[i], color: e.target.value };
                      onUpdateObject(selectedObj.id, { gradient: { ...selectedObj.gradient, stops } });
                    }} className="w-5 h-5 rounded border-0 p-0 cursor-pointer" />
                    <input type="range" min="0" max="1" step="0.01" value={stop.offset} onChange={(e) => {
                      const stops = [...selectedObj.gradient.stops];
                      stops[i] = { ...stops[i], offset: Number(e.target.value) };
                      onUpdateObject(selectedObj.id, { gradient: { ...selectedObj.gradient, stops } });
                    }} className="flex-1 accent-teal-600" />
                    <input type="range" min="0" max="1" step="0.1" value={stop.opacity ?? 1} onChange={(e) => {
                      const stops = [...selectedObj.gradient.stops];
                      stops[i] = { ...stops[i], opacity: Number(e.target.value) };
                      onUpdateObject(selectedObj.id, { gradient: { ...selectedObj.gradient, stops } });
                    }} className="w-10 accent-blue-500" title="Stop opacity" />
                  </div>
                ))}
                <button onClick={() => {
                  const stops = [...(selectedObj.gradient.stops || []), { offset: 0.5, color: '#3B82F6', opacity: 1 }];
                  onUpdateObject(selectedObj.id, { gradient: { ...selectedObj.gradient, stops } });
                }} className="text-[9px] text-indigo-400 hover:underline">+ Add Stop</button>
              </div>
            )}
          </div>

          {/* Pattern Fill */}
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Pattern Fill</span>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {PATTERN_TYPES.map(p => (
                <button key={p} onClick={() => onApplyPattern(p)}
                  className="px-2 py-1 text-[9px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-300 capitalize">{p}</button>
              ))}
            </div>
          </div>

          {/* Opacity + Blend Mode */}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 w-12">Opacity</span>
              <input type="range" min="0" max="1" step="0.05" value={currentStyle.opacity}
                onChange={(e) => handleChange('opacity', Number(e.target.value))} className="flex-1 accent-teal-600" />
              <span className="text-[9px] text-slate-400 w-7 text-right font-mono">{Math.round(currentStyle.opacity * 100)}%</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] text-slate-400 w-12">Blend</span>
              <select value={selectedObj?.blendMode || 'normal'} onChange={(e) => handleChange('blendMode', e.target.value)}
                className="input-field flex-1 text-[9px] px-1.5 py-0.5" data-testid="blend-mode-select">
                {BLEND_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* STROKE SECTION */}
      {activeSection === 'stroke' && (
        <div className="p-3 space-y-3">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Stroke Color</span>
            <div className="flex items-center gap-2 mt-1.5">
              <input type="color" value={currentStyle.stroke} onChange={(e) => handleChange('stroke', e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border border-slate-200 p-0" />
              <input value={currentStyle.stroke} onChange={(e) => handleChange('stroke', e.target.value)}
                className="input-field flex-1 text-[10px] font-mono px-2 py-1" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400 w-10">Width</span>
              <input type="range" min="0" max="20" step="0.5" value={currentStyle.strokeWidth}
                onChange={(e) => handleChange('strokeWidth', Number(e.target.value))} className="flex-1 accent-teal-600" />
              <span className="text-[9px] text-slate-400 w-5 text-right font-mono">{currentStyle.strokeWidth}</span>
            </div>
          </div>
          <div>
            <span className="text-[10px] text-slate-400">Dash</span>
            <select value={currentStyle.dashStyle || ''} onChange={(e) => handleChange('dashStyle', e.target.value)}
              className="input-field w-full text-[10px] px-1.5 py-1 mt-0.5">
              {DASH_STYLES.map(d => <option key={d.id} value={d.value}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <span className="text-[10px] text-slate-400">Line Cap</span>
            <div className="flex gap-1 mt-0.5">
              {LINE_CAPS.map(c => (
                <button key={c} onClick={() => handleChange('lineCap', c)}
                  className={`flex-1 px-1 py-1 text-[9px] rounded ${currentStyle.lineCap === c ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800 text-slate-400'}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-[10px] text-slate-400">Line Join</span>
            <div className="flex gap-1 mt-0.5">
              {LINE_JOINS.map(j => (
                <button key={j} onClick={() => handleChange('lineJoin', j)}
                  className={`flex-1 px-1 py-1 text-[9px] rounded ${(currentStyle.lineJoin || 'round') === j ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800 text-slate-400'}`}>{j}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-[10px] text-slate-400">Width Profile</span>
            <div className="space-y-0.5 mt-0.5">
              {VARIABLE_WIDTH_PROFILES.map(p => (
                <button key={p.id} onClick={() => handleChange('widthProfile', p.id)}
                  className={`w-full text-left px-2 py-1 text-[9px] rounded flex items-center gap-2 ${(currentStyle.widthProfile || 'uniform') === p.id ? 'bg-indigo-900/30 text-indigo-300' : 'text-slate-400 hover:bg-slate-800'}`}>
                  <svg width="40" height="8" viewBox="0 0 40 8">
                    {p.widths.map((w, i) => (
                      <rect key={i} x={i * 8} y={4 - w * 3} width="7" height={w * 6} fill="currentColor" rx="1" />
                    ))}
                  </svg>
                  <span>{p.name}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Arrow heads */}
          {selectedObj && (selectedObj.type === 'arrow' || selectedObj.type?.startsWith('connector')) && (
            <div>
              <span className="text-[10px] text-slate-400">Arrow Head</span>
              <div className="flex gap-1 mt-0.5 flex-wrap">
                {ARROW_STYLES.map(a => (
                  <button key={a.id} onClick={() => handleChange('arrowEnd', a.id)}
                    className={`px-2 py-1 text-[9px] rounded ${(selectedObj?.arrowEnd || currentStyle.arrowEnd) === a.id ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800 text-slate-400'}`}>{a.name}</button>
                ))}
              </div>
              <div className="mt-1.5">
                <span className="text-[9px] text-slate-400">Label</span>
                <input value={selectedObj?.label || ''} onChange={(e) => onUpdateObject(selectedObj.id, { label: e.target.value })}
                  className="input-field w-full text-[10px] px-2 py-1 mt-0.5" placeholder="Label..." />
              </div>
            </div>
          )}
          {selectedObj?.type === 'rect' && (
            <div>
              <span className="text-[10px] text-slate-400">Corner Radius</span>
              <div className="flex items-center gap-2 mt-0.5">
                <input type="range" min="0" max="50" value={selectedObj.rx || 0}
                  onChange={(e) => onUpdateObject(selectedObj.id, { rx: Number(e.target.value) })} className="flex-1 accent-teal-600" />
                <span className="text-[9px] text-slate-400 w-5 font-mono">{selectedObj.rx || 0}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* EFFECTS SECTION */}
      {activeSection === 'effects' && (
        <div className="p-3 space-y-3">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Effect Presets</span>
          <div className="space-y-0.5">
            {EFFECT_PRESETS.map(p => (
              <button key={p.name} onClick={() => onApplyEffect(p.effects)}
                className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-indigo-900/30 rounded text-slate-300 hover:text-indigo-300"
                data-testid={`effect-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
                {p.name}
              </button>
            ))}
          </div>
          {selectedObj?.effects && selectedObj.effects.length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Active Effects</span>
              {selectedObj.effects.map((fx, i) => (
                <div key={i} className="flex items-center gap-1 mt-1 p-1.5 bg-slate-800 rounded text-[10px]">
                  <Sparkle size={10} className="text-teal-500" />
                  <span className="flex-1 capitalize text-slate-300">{fx.type}</span>
                  <button onClick={() => onRemoveEffect(i)} className="text-red-400 hover:text-red-600"><X size={10} /></button>
                </div>
              ))}
              <button onClick={() => onRemoveEffect('all')} className="text-[9px] text-red-400 hover:underline mt-1">Remove All</button>
            </div>
          )}
          <div>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Custom Effect</span>
            <div className="space-y-1.5 mt-1">
              <button onClick={() => onApplyEffect([{ type: 'dropShadow', dx: 3, dy: 3, blur: 4, color: '#000000', opacity: 0.3 }])}
                className="w-full text-left px-2 py-1 text-[10px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-300">+ Drop Shadow</button>
              <button onClick={() => onApplyEffect([{ type: 'blur', radius: 4 }])}
                className="w-full text-left px-2 py-1 text-[10px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-300">+ Gaussian Blur</button>
              <button onClick={() => onApplyEffect([{ type: 'glow', radius: 6, color: '#0D9488', opacity: 0.5 }])}
                className="w-full text-left px-2 py-1 text-[10px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-300">+ Glow</button>
            </div>
          </div>
        </div>
      )}

      {/* TYPOGRAPHY SECTION */}
      {activeSection === 'type' && selectedObj?.type === 'text' && (
        <div className="p-3 space-y-2.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Font</span>
          <select value={selectedObj.fontFamily || 'IBM Plex Sans'} onChange={(e) => onUpdateObject(selectedObj.id, { fontFamily: e.target.value })}
            className="input-field w-full text-[10px] px-1.5 py-1">
            {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <div className="flex gap-1">
            <input type="number" value={selectedObj.fontSize || 16} min="6" max="120"
              onChange={(e) => onUpdateObject(selectedObj.id, { fontSize: Number(e.target.value) })}
              className="input-field w-14 text-[10px] px-1.5 py-1" />
            <button onClick={() => onUpdateObject(selectedObj.id, { fontWeight: selectedObj.fontWeight === 'bold' ? 'normal' : 'bold' })}
              className={`px-2 py-1 text-[9px] rounded font-bold ${selectedObj.fontWeight === 'bold' ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800'}`}>B</button>
            <button onClick={() => onUpdateObject(selectedObj.id, { fontStyle: selectedObj.fontStyle === 'italic' ? 'normal' : 'italic' })}
              className={`px-2 py-1 text-[9px] rounded italic ${selectedObj.fontStyle === 'italic' ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800'}`}>I</button>
            <button onClick={() => onUpdateObject(selectedObj.id, { baselineShift: selectedObj.baselineShift === 'super' ? 'auto' : 'super' })}
              className={`px-1.5 py-1 text-[8px] rounded ${selectedObj.baselineShift === 'super' ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800'}`}>X<sup>2</sup></button>
            <button onClick={() => onUpdateObject(selectedObj.id, { baselineShift: selectedObj.baselineShift === 'sub' ? 'auto' : 'sub' })}
              className={`px-1.5 py-1 text-[8px] rounded ${selectedObj.baselineShift === 'sub' ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800'}`}>X<sub>2</sub></button>
          </div>
          <div className="flex gap-1">
            {['left', 'center', 'right'].map(a => (
              <button key={a} onClick={() => onUpdateObject(selectedObj.id, { textAlign: a })}
                className={`flex-1 px-2 py-1 text-[9px] rounded capitalize ${(selectedObj.textAlign || 'left') === a ? 'bg-indigo-900/40 text-indigo-300' : 'bg-slate-800 text-slate-400'}`}>{a}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-slate-400 w-12">Kerning</span>
            <input type="range" min="-5" max="10" step="0.1" value={selectedObj.letterSpacing || 0}
              onChange={(e) => onUpdateObject(selectedObj.id, { letterSpacing: Number(e.target.value) })} className="flex-1 accent-teal-600" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-slate-400 w-12">Leading</span>
            <input type="range" min="0.8" max="3" step="0.1" value={selectedObj.lineHeight || 1.4}
              onChange={(e) => onUpdateObject(selectedObj.id, { lineHeight: Number(e.target.value) })} className="flex-1 accent-teal-600" />
          </div>
          <textarea value={selectedObj.text || ''} onChange={(e) => onUpdateObject(selectedObj.id, { text: e.target.value })}
            className="input-field w-full text-[10px] min-h-[60px]" data-testid="edit-text-input" />
        </div>
      )}
      {activeSection === 'type' && (!selectedObj || selectedObj.type !== 'text') && (
        <div className="p-6 text-center text-[10px] text-slate-400">Select a text object to edit typography</div>
      )}

      {/* SYMBOLS SECTION */}
      {activeSection === 'symbols' && (
        <div className="p-3 space-y-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Components</span>
          {selectedObj && (
            <button onClick={onSaveSymbol} className="w-full text-left px-2 py-1.5 text-[10px] bg-indigo-900/30 text-indigo-300 hover:bg-indigo-900/40 rounded" data-testid="save-symbol-btn">
              + Save Selection as Symbol
            </button>
          )}
          {symbols && symbols.length > 0 ? (
            <div className="space-y-0.5">
              {symbols.map(s => (
                <button key={s.id} onClick={() => onInstantiateSymbol(s)}
                  className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-indigo-900/30 rounded text-slate-300 truncate">
                  {s.name} ({s.objects.length} objects)
                </button>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-slate-400 py-2">No saved symbols. Select objects and save.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== RIGHT PANEL ====================
export const EditorRightPanel = ({
  objects, selectedIds, selectedObj, onUpdateObject, onSelectObject,
  onMoveLayer, onToggleVisibility, onToggleLock, onDeleteObject,
  onGroup, onUngroup,
  figures, onLoadFigure, onDeleteFigure, onLoadTemplate,
  showAI, aiPrompt, setAiPrompt, aiSuggestion, aiLoading, onAIAssist
}) => {
  const [tab, setTab] = useState('layers');

  return (
    <div className="w-52 bg-white border-l border-slate-200 overflow-y-auto flex-shrink-0" style={{ height: 'calc(100vh - 200px)' }}>
      <div className="flex border-b border-slate-200 sticky top-0 bg-white z-10">
        {['layers', 'transform', 'templates', ...(showAI ? ['ai'] : [])].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 px-1 py-1.5 text-[9px] uppercase tracking-wider font-semibold ${tab === t ? 'text-indigo-300 border-b-2 border-indigo-400' : 'text-slate-400'}`}>
            {t === 'ai' ? 'AI' : t}
          </button>
        ))}
      </div>

      {tab === 'layers' && (
        <div className="p-2">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] text-slate-400 font-semibold">{objects.length} objects</span>
            <div className="flex gap-0.5">
              {selectedIds.length >= 2 && (
                <button onClick={onGroup} className="px-1.5 py-0.5 text-[8px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-400" title="Group">G</button>
              )}
              {selectedObj?.type === 'group' && (
                <button onClick={onUngroup} className="px-1.5 py-0.5 text-[8px] bg-slate-800 hover:bg-indigo-900/30 rounded text-slate-400" title="Ungroup">UG</button>
              )}
            </div>
          </div>
          <div className="space-y-0.5">
            {[...objects].reverse().map(obj => (
              <div key={obj.id}
                className={`flex items-center gap-1 px-1.5 py-1 rounded text-[10px] cursor-pointer group ${selectedIds.includes(obj.id) ? 'bg-indigo-900/30 text-indigo-300' : 'text-slate-300 hover:bg-slate-800'}`}
                onClick={() => onSelectObject(obj.id)}>
                <button onClick={(e) => { e.stopPropagation(); onToggleVisibility(obj.id); }} className="p-0.5">
                  {obj.visible !== false ? <Eye size={10} /> : <EyeSlash size={10} className="text-slate-300" />}
                </button>
                <button onClick={(e) => { e.stopPropagation(); onToggleLock(obj.id); }} className="p-0.5">
                  {obj.locked ? <Lock size={10} className="text-amber-500" /> : <LockOpen size={10} className="text-slate-300" />}
                </button>
                <span className="truncate flex-1 capitalize">
                  {obj.label || obj.type}{obj.type === 'text' ? ` "${(obj.text || '').substring(0, 8)}"` : ''}
                </span>
                <button onClick={(e) => { e.stopPropagation(); onDeleteObject(obj.id); }} className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash size={10} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'transform' && selectedObj && (
        <div className="p-3 space-y-2.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Position & Size</span>
          <div className="grid grid-cols-2 gap-1.5">
            {selectedObj.x !== undefined && <>
              <div><label className="text-[8px] text-slate-400">X</label><input type="number" value={Math.round(selectedObj.x)} onChange={(e) => onUpdateObject(selectedObj.id, { x: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
              <div><label className="text-[8px] text-slate-400">Y</label><input type="number" value={Math.round(selectedObj.y)} onChange={(e) => onUpdateObject(selectedObj.id, { y: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
            </>}
            {selectedObj.w !== undefined && <>
              <div><label className="text-[8px] text-slate-400">W</label><input type="number" value={Math.round(selectedObj.w)} onChange={(e) => onUpdateObject(selectedObj.id, { w: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
              <div><label className="text-[8px] text-slate-400">H</label><input type="number" value={Math.round(selectedObj.h)} onChange={(e) => onUpdateObject(selectedObj.id, { h: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
            </>}
            {selectedObj.x1 !== undefined && <>
              <div><label className="text-[8px] text-slate-400">X1</label><input type="number" value={Math.round(selectedObj.x1)} onChange={(e) => onUpdateObject(selectedObj.id, { x1: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
              <div><label className="text-[8px] text-slate-400">Y1</label><input type="number" value={Math.round(selectedObj.y1)} onChange={(e) => onUpdateObject(selectedObj.id, { y1: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
              <div><label className="text-[8px] text-slate-400">X2</label><input type="number" value={Math.round(selectedObj.x2)} onChange={(e) => onUpdateObject(selectedObj.id, { x2: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
              <div><label className="text-[8px] text-slate-400">Y2</label><input type="number" value={Math.round(selectedObj.y2)} onChange={(e) => onUpdateObject(selectedObj.id, { y2: Number(e.target.value) })} className="input-field w-full text-[10px] px-1.5 py-0.5 font-mono" /></div>
            </>}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] text-slate-400 w-10">Rotate</span>
            <input type="range" min="0" max="360" value={selectedObj.rotation || 0}
              onChange={(e) => onUpdateObject(selectedObj.id, { rotation: Number(e.target.value) })} className="flex-1 accent-teal-600" />
            <input type="number" value={selectedObj.rotation || 0} className="input-field w-10 text-[9px] px-1 py-0.5 font-mono"
              onChange={(e) => onUpdateObject(selectedObj.id, { rotation: Number(e.target.value) })} />
          </div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <button onClick={() => onMoveLayer(selectedObj.id, 'up')} className="px-2 py-1 text-[9px] bg-slate-800 hover:bg-slate-700 rounded text-slate-300">Bring Fwd</button>
            <button onClick={() => onMoveLayer(selectedObj.id, 'down')} className="px-2 py-1 text-[9px] bg-slate-800 hover:bg-slate-700 rounded text-slate-300">Send Back</button>
          </div>
        </div>
      )}
      {tab === 'transform' && !selectedObj && <div className="p-6 text-center text-[10px] text-slate-400">Select an object</div>}

      {tab === 'templates' && (
        <div className="p-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-1">Bio Templates</span>
          <div className="space-y-0.5 mt-1">
            {Object.keys(BIO_TEMPLATES).map(name => (
              <button key={name} onClick={() => onLoadTemplate(name)}
                className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-indigo-900/30 rounded text-slate-300 hover:text-indigo-300"
                data-testid={`template-${name.replace(/\s+/g, '-').toLowerCase()}`}>{name}</button>
            ))}
          </div>
          {figures.length > 0 && (
            <>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-1 mt-3 block">Saved Figures</span>
              <div className="space-y-0.5 mt-1">
                {figures.map(fig => (
                  <div key={fig.id} className="flex items-center justify-between px-2 py-1 hover:bg-slate-800 rounded text-[10px]">
                    <button onClick={() => onLoadFigure(fig)} className="truncate flex-1 text-left text-slate-300">{fig.name}</button>
                    <button onClick={() => onDeleteFigure(fig.id)} className="p-0.5 text-red-400"><Trash size={10} /></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'ai' && showAI && (
        <div className="p-3">
          <span className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold flex items-center gap-1"><Lightning size={11} /> AI Assistant</span>
          <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
            className="input-field w-full text-[10px] min-h-[70px] mt-2" placeholder="Describe the figure..." data-testid="ai-prompt-input" />
          <Button onClick={onAIAssist} className="btn-primary w-full text-[10px] mt-1.5 py-1.5" disabled={aiLoading} data-testid="ai-suggest-btn">
            {aiLoading ? 'Generating...' : 'Get Suggestions'}
          </Button>
          {aiSuggestion && <div className="mt-2 p-2 bg-indigo-900/30/50 rounded-md text-[10px] text-slate-300 max-h-44 overflow-y-auto whitespace-pre-wrap" data-testid="ai-suggestion">{aiSuggestion}</div>}
        </div>
      )}
    </div>
  );
};

// ==================== EXPORT DIALOG ====================
export const ExportDialog = ({ onClose, onExport }) => {
  const [format, setFormat] = useState('svg');
  const [dpi, setDpi] = useState(300);
  const [preset, setPreset] = useState('default');
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl" data-testid="export-dialog">
        <div className="flex items-center justify-between mb-4">
          <h2 className="heading-card">Export Figure</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-700 rounded-lg"><X size={16} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-200">Format</label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {['svg', 'png', 'pdf'].map(f => (
                <button key={f} onClick={() => setFormat(f)} className={`py-2 text-xs uppercase font-semibold rounded-lg ${format === f ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300'}`}>{f}</button>
              ))}
            </div>
          </div>
          {format === 'png' && (
            <div><label className="text-xs font-medium text-slate-200">Resolution (DPI)</label>
              <div className="flex gap-2 mt-1.5">{[150, 300, 600].map(d => (
                <button key={d} onClick={() => setDpi(d)} className={`flex-1 py-2 text-xs font-medium rounded-lg ${dpi === d ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300'}`}>{d}</button>
              ))}</div></div>
          )}
          {format === 'pdf' && (
            <div><label className="text-xs font-medium text-slate-200">Journal Preset</label>
              <select value={preset} onChange={(e) => setPreset(e.target.value)} className="input-field w-full mt-1.5 text-sm">
                <option value="default">Default (A4)</option><option value="nature">Nature</option><option value="science">Science</option><option value="cell">Cell</option>
              </select></div>
          )}
          <Button onClick={() => { onExport(format, { dpi, preset }); onClose(); }} className="btn-primary w-full" data-testid="export-confirm-btn">
            <Download size={14} className="mr-2" /> Export {format.toUpperCase()}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTEXT MENU ====================
export const ContextMenu = ({ x, y, onClose, actions }) => (
  <div className="fixed bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50 min-w-[160px]" style={{ left: x, top: y }}
    onMouseLeave={onClose} data-testid="context-menu">
    {actions.map((a, i) => a.separator ? (
      <div key={i} className="border-t border-slate-100 my-1" />
    ) : (
      <button key={i} onClick={() => { a.action(); onClose(); }} disabled={a.disabled}
        className="w-full text-left px-3 py-1.5 text-[11px] text-slate-300 hover:bg-indigo-900/30 hover:text-indigo-300 disabled:opacity-30 disabled:cursor-default flex items-center justify-between">
        <span>{a.label}</span>
        {a.shortcut && <span className="text-[9px] text-slate-400 ml-4">{a.shortcut}</span>}
      </button>
    ))}
  </div>
);
